import type {
  GetActiveSessionResponse,
  GetSessionSnapshotResponse,
} from "@nlc/shared-types";

import {
  useMutation,
  type useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { uploadingSessionKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import type { FileResult } from "../types";
import type { FileVersionMetadata } from "../types/fileVersionMetadata";
import type { InFlightUpload, SessionFileRow } from "../types/sessionFile";

import {
  discardSession,
  overrideEvaluation,
  submitSession,
} from "../api/confidence-checker.api";
import { mergeResultsWithSessionFiles } from "../utils/mergeResultsWithSessionFiles";
import { mergeSessionFiles } from "../utils/mergeSessionFiles";
import { useConfidenceCheckerMyFiles } from "./useConfidenceCheckerMyFiles";
import { useSessionFileMetadata } from "./useSessionFileMetadata";
import { useSessionUploads } from "./useSessionUploads";
import { useUploadingSessionQueries } from "./useUploadingSessionQueries";

/** @deprecated Use `InFlightUpload` — kept for incremental migration. */
export type PendingUpload = InFlightUpload;

interface SubmitSessionOptions {
  mentionedUserIds?: number[];
  message?: string;
  postOnActivityLog: boolean;
}

interface UseConfidenceCheckerSessionOptions {
  isOpen: boolean;
  requestId: number;
}

interface UseConfidenceCheckerSessionReturn {
  activeSessionQuery: ReturnType<typeof useQuery<GetActiveSessionResponse>>;
  addFiles: (files: File[]) => Promise<void>;
  cancelUpload: (clientId: string) => void;
  discardAndClose: (onDone: () => void) => Promise<void>;
  /** Snapshot results overlaid with in-flight upload/replace progress. */
  displayResults: FileResult[];
  hasResolvedResume: boolean;
  isSubmitting: boolean;
  override: (evaluationId: number) => Promise<void>;
  pendingUploads: PendingUpload[];
  removeFile: (input: {
    attachmentId?: number;
    clientId: string;
  }) => Promise<void>;
  replaceFile: (input: { attachmentId: number; file: File }) => void;
  results: FileResult[];
  resumeSession: () => Promise<void>;
  /** Merged snapshot + in-flight uploads — canonical file list for all steps. */
  sessionFiles: SessionFileRow[];
  sessionId: null | string;
  snapshot: GetSessionSnapshotResponse | undefined;
  snapshotQuery: ReturnType<typeof useQuery<GetSessionSnapshotResponse>>;
  startFresh: () => Promise<void>;
  submit: (options: SubmitSessionOptions) => Promise<string>;
  updateFileMetadata: (
    clientId: string,
    patch: Partial<FileVersionMetadata>,
  ) => void;
}

export function useConfidenceCheckerSession({
  isOpen,
  requestId,
}: UseConfidenceCheckerSessionOptions): UseConfidenceCheckerSessionReturn {
  const queryClient = useQueryClient();
  const { data: userData } = useUser();
  const designerId = userData?.user?.id;
  const { data: requestMyFiles } = useConfidenceCheckerMyFiles(requestId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const localFilesRef = useRef<Map<number, File>>(new Map());

  const {
    activeSessionQuery,
    claimSessionId,
    hasResolvedResume,
    invalidateSnapshot,
    invalidateSnapshotFor,
    results,
    resumeSession,
    sessionId,
    setHasResolvedResume,
    setSessionId,
    snapshot,
    snapshotQuery,
    startFresh,
    syncSnapshot,
  } = useUploadingSessionQueries({
    isOpen,
    localFilesRef,
    requestId,
  });

  const {
    carryOverOnAttach,
    clearMetadata,
    fileMetadataRef,
    metadataVersion,
    updateFileMetadata,
  } = useSessionFileMetadata(snapshot);

  const {
    addFiles,
    cancelUpload,
    pendingUploads,
    removeFile,
    replaceFile,
    resetUploadState,
  } = useSessionUploads({
    carryOverOnAttach,
    claimSessionId,
    designerId,
    fileMetadataRef,
    invalidateSnapshot,
    invalidateSnapshotFor,
    localFilesRef,
    requestId,
    requestMyFiles,
    sessionId,
    setSessionId,
    snapshot,
    syncSnapshot,
    updateFileMetadata,
  });

  const sessionFiles = useMemo<SessionFileRow[]>(
    () =>
      mergeSessionFiles({
        fileMetadata: fileMetadataRef.current,
        inFlightUploads: pendingUploads,
        localFiles: localFilesRef.current,
        snapshot: snapshotQuery.data,
      }),
    [fileMetadataRef, metadataVersion, pendingUploads, snapshotQuery.data],
  );

  const displayResults = useMemo<FileResult[]>(
    () => mergeResultsWithSessionFiles(results, sessionFiles),
    [results, sessionFiles],
  );

  const overrideMutation = useMutation({
    mutationFn: (evaluationId: number) => overrideEvaluation(evaluationId),
    onSuccess: invalidateSnapshot,
  });
  const override = useCallback(
    async (evaluationId: number) => {
      await overrideMutation.mutateAsync(evaluationId);
    },
    [overrideMutation],
  );

  const submit = useCallback(
    async (options: SubmitSessionOptions): Promise<string> => {
      if (!sessionId || !snapshotQuery.data) {
        throw new Error("Session not ready to submit");
      }

      const mentionedUserIds = options.mentionedUserIds?.filter((id) => id > 0);

      setIsSubmitting(true);
      try {
        await submitSession(sessionId, {
          attachmentIds: snapshotQuery.data.attachments.map(
            (entry) => entry.attachment.id,
          ),
          mentionedUserIds:
            mentionedUserIds && mentionedUserIds.length > 0
              ? mentionedUserIds
              : undefined,
          message: options.message,
          postOnActivityLog: options.postOnActivityLog,
          requestId,
        });
        void queryClient.invalidateQueries({
          queryKey: uploadingSessionKeys.all,
        });
        void queryClient.invalidateQueries({
          queryKey: ["myFilesStructure"],
        });

        return sessionId;
      } finally {
        setIsSubmitting(false);
      }
    },
    [queryClient, requestId, sessionId, snapshotQuery.data],
  );

  const resetLocalState = useCallback(() => {
    resetUploadState();
    setSessionId(null);
    clearMetadata();
    setHasResolvedResume(false);
  }, [clearMetadata, resetUploadState, setHasResolvedResume, setSessionId]);

  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: uploadingSessionKeys.active(requestId),
      });
    },
    [queryClient, requestId],
  );

  const discardAndClose = useCallback(
    async (onDone: () => void) => {
      const activeId =
        sessionId ?? activeSessionQuery.data?.session?.id ?? null;

      if (activeId) {
        try {
          await discardSession(activeId);
        } catch {
          /* swallow — closing UX should not surface 4xx on already-discarded sessions */
        }
        void queryClient.invalidateQueries({
          queryKey: uploadingSessionKeys.all,
        });
      }
      resetLocalState();
      onDone();
    },
    [
      activeSessionQuery.data?.session?.id,
      queryClient,
      resetLocalState,
      sessionId,
    ],
  );

  return {
    activeSessionQuery,
    addFiles,
    cancelUpload,
    discardAndClose,
    displayResults,
    hasResolvedResume,
    isSubmitting,
    override,
    pendingUploads,
    removeFile,
    replaceFile,
    results,
    resumeSession,
    sessionFiles,
    sessionId,
    snapshot,
    snapshotQuery,
    startFresh,
    submit,
    updateFileMetadata,
  };
}
